/**
 * useClaimId - Single Source of Truth for claim identity.
 *
 * Resolves the claim ID once at wizard mount and rewrites the URL to ?claim=<id>.
 * All other sources (?draft=, ?resume=, location.state, localStorage) are treated
 * as backward-compat aliases and silently normalized.
 *
 * If no ID is found anywhere, creates a new claim via POST /api/v1/claims.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import api from '../../../../utils/api';

interface UseClaimIdResult {
  claimId: string | null;
  isResolving: boolean;
  error: string | null;
}

interface LocationState {
  draftId?: string;
  freshDraftData?: any;
  fromDashboard?: boolean;
  // Set by "+ New Claim" buttons (AppLayout sidebar/header,
  // DashboardNew empty state, AccountantLayout) to bypass the
  // resume-via-/drafts/latest path that PR #197 + sweep #198 made
  // reliable but which silently hijacks explicit "create new" intent
  // into resuming the most recent in-progress case.
  forceNew?: boolean;
}

export function useClaimId(): UseClaimIdResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [claimId, setClaimId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resolvedRef = useRef(false);

  const resolve = useCallback(async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;

    // 1. ?claim= param (canonical)
    const claimParam = searchParams.get('claim');
    if (claimParam) {
      setClaimId(claimParam);
      setIsResolving(false);
      return;
    }

    // 2. ?draft= or ?draft_id= (backward compat aliases)
    const draftParam = searchParams.get('draft') || searchParams.get('draft_id');
    if (draftParam) {
      rewriteUrl(draftParam);
      setClaimId(draftParam);
      setIsResolving(false);
      return;
    }

    // 3. ?resume= (report resume — treat as claim alias)
    const resumeParam = searchParams.get('resume');
    if (resumeParam) {
      rewriteUrl(resumeParam);
      setClaimId(resumeParam);
      setIsResolving(false);
      return;
    }

    // 4. Navigation state from dashboard
    const navState = location.state as LocationState | null;
    if (navState?.draftId) {
      rewriteUrl(navState.draftId);
      setClaimId(navState.draftId);
      setIsResolving(false);
      return;
    }

    // 4b. Explicit "+ New Claim" intent — skip the resume probe and go
    //     straight to claim creation. Set by buttons in AppLayout
    //     (sidebar nav + header), DashboardNew empty state, and
    //     AccountantLayout. Without this, the resume-via-/drafts/latest
    //     path below silently hijacks "+ New Claim" into resuming the
    //     most recent in-progress case (Bug 1, 2026-04-26 — surfaced
    //     once PR #197 made /drafts/latest reliable AND PR #198 swept
    //     the empty intake drafts that previously masked the issue).
    if (navState?.forceNew) {
      console.log('🆕 forceNew flag — skipping resume probe, creating fresh claim.');
      // Fall through to step 6 (creation) without touching /drafts/latest.
    } else {
      // 5. No ID anywhere. Before creating a new claim, check whether
      //    the user already has a recent in-progress draft we can
      //    resume. Prevents the duplicate-draft pattern flagged by
      //    audit 2026-04-25: page refresh / stale tab / SavedWorkCard
      //    error race would each spawn a new empty case otherwise.
      //    Forensic git showed 4 duplicate "Test run" reports spawned
      //    this way over 3 days.
      try {
        const recent = await api.get<{ draft_id?: string; data?: any }>(
          '/api/v1/reports/drafts/latest',
          { showErrorToast: false }
        );
        if (recent?.draft_id) {
          console.log('🔁 Resuming recent draft instead of spawning new:', recent.draft_id);
          rewriteUrl(recent.draft_id);
          setClaimId(recent.draft_id);
          setIsResolving(false);
          return;
        }
      } catch {
        // No recent draft (404 or empty) — fall through to create.
      }
    }

    // 6. No recent draft either — create a fresh claim. Server-side
    //    idempotency in claim_service.create_claim is the safety net.
    try {
      const result = await api.post<{ claim_id: string }>('/api/v1/claims', {}, { showErrorToast: false });
      if (result?.claim_id) {
        rewriteUrl(result.claim_id);
        setClaimId(result.claim_id);
      } else {
        throw new Error('No claim_id in response');
      }
    } catch (e: any) {
      console.error('Failed to create new claim:', e);
      setError(e.message || 'Failed to create claim');

      // If auth wasn't ready yet (race condition), wait and retry once
      if (e.message?.includes('401') || e.message?.includes('authentication') || e.message?.includes('Missing')) {
        console.log('🔄 Auth may not be ready yet, retrying in 1s...');
        await new Promise(r => setTimeout(r, 1000));
        try {
          const retry = await api.post<{ claim_id: string }>('/api/v1/claims', {}, { showErrorToast: false });
          if (retry?.claim_id) {
            rewriteUrl(retry.claim_id);
            setClaimId(retry.claim_id);
            setError(null);
            setIsResolving(false);
            return;
          }
        } catch { /* fall through to legacy fallback */ }
      }

      // Ultimate fallback: try legacy draft endpoint
      try {
        const draft = await api.post<{ draft_id: string }>('/api/v1/reports/drafts/save', {
          step: 1,
          data: {},
          last_modified: new Date().toISOString(),
        }, { showErrorToast: false });
        if (draft?.draft_id) {
          rewriteUrl(draft.draft_id);
          setClaimId(draft.draft_id);
          setError(null);
        }
      } catch {
        // Already set error above
      }
    } finally {
      setIsResolving(false);
    }
  }, []); // Empty deps — resolve once on mount

  /**
   * Rewrite URL to use canonical ?claim= param.
   * Preserves other params (?step=, ?section=).
   */
  function rewriteUrl(id: string) {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('claim', id);
    // Remove legacy aliases
    newParams.delete('draft');
    newParams.delete('draft_id');
    newParams.delete('resume');
    setSearchParams(newParams, { replace: true });
  }

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { claimId, isResolving, error };
}
